import { useId, useState } from 'react';
import { Input, Select } from '@/components/ui/FormControls';
import ComboSelect from '@/components/common/ComboSelect';
import FieldHint from '@/components/common/FieldHint';
import { Sparkles } from 'lucide-react';
import type { JerseyDetailsV2, JerseyEntryContext, JerseySkuKind } from '@klothiq/domain';
import { jerseyEntryPrintToken } from '@klothiq/domain';

export default function JerseyDetailsFields({
  value,
  context,
  onChange,
  errors = {},
}: {
  value: JerseyDetailsV2;
  context: JerseyEntryContext;
  onChange: (value: JerseyDetailsV2) => void;
  errors?: Record<string, string>;
}) {
  const prefix = useId();
  const [periodKind, setPeriodKind] = useState(value.period?.kind || '');
  const update = (patch: Partial<JerseyDetailsV2>) => onChange({ ...value, ...patch });
  const error = (key: string) =>
    errors[key] && (
      <p id={`${prefix}-${key}-error`} role="alert" className="text-sm text-destructive">
        {errors[key]}
      </p>
    );
  const select = (kind: JerseySkuKind, key: keyof JerseyDetailsV2, label: string) => {
    const entries = (context.identifier_config?.tokens[kind] ?? []).filter(
      (t) => t.active !== false && t.key !== 'UNSPECIFIED',
    );
    const selected = typeof value[key] === 'string' ? (value[key] as string) : '';
    return (
      <div className="space-y-1" key={key}>
        <label className="block text-sm" htmlFor={`${prefix}-${key}`}>
          {label} <span className="text-xs text-muted-foreground">(optional)</span>
        </label>
        <Select
          id={`${prefix}-${key}`}
          className="w-full"
          value={selected}
          helpLabel={`${label} (optional)`}
          aria-invalid={!!errors[key]}
          aria-describedby={errors[key] ? `${prefix}-${key}-error` : undefined}
          onChange={(e) => update({ [key]: e.target.value || null })}
        >
          <option value="">Not specified</option>
          {selected && !entries.some((t) => t.key === selected) && (
            <option value={selected}>{selected} (saved value)</option>
          )}
          {entries.map((t) => (
            <option key={t.key} value={t.key}>
              {t.label}
            </option>
          ))}
        </Select>
        {error(key)}
      </div>
    );
  };
  return (
    <section
      className="glass-card space-y-5 border-primary/20 p-4 sm:p-6"
      aria-labelledby={`${prefix}-heading`}
    >
      <div className="flex items-start gap-3">
        <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
        <div>
          <h2 id={`${prefix}-heading`} className="font-semibold">
            Jersey details{' '}
            <span className="text-sm font-normal text-muted-foreground">(optional)</span>
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Fill any details for consistent suggestions, or skip this section and type the product
            name below.
          </p>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1">
          <label className="block text-sm" htmlFor={`${prefix}-team`}>
            Club / country / team <span className="text-xs text-muted-foreground">(optional)</span>
          </label>
          <ComboSelect
            id={`${prefix}-team`}
            value={
              value.team?.id || (value.team?.entered_name ? `typed:${value.team.entered_name}` : '')
            }
            className="min-h-11"
            placeholder="Search or type a team"
            searchPlaceholder="Barcelona, Brazil…"
            hasError={!!errors.team}
            aria-describedby={errors.team ? `${prefix}-team-error` : undefined}
            helpLabel="Club / country / team (optional)"
            options={[
              ...context.teams.map((t) => ({ value: t.id, label: t.name, sublabel: t.code })),
              ...(value.team?.entered_name && !value.team.id
                ? [
                    {
                      value: `typed:${value.team.entered_name}`,
                      label: value.team.entered_name,
                      sublabel: 'Entered name',
                    },
                  ]
                : []),
            ]}
            createLabel={(term) => `Use “${term}” without a saved team`}
            onCreateOption={(term) => update({ team: { entered_name: term, id: null } })}
            onChange={(id) => {
              const team = context.teams.find((t) => t.id === id);
              update({
                team: team
                  ? { id: team.id, entered_name: team.name }
                  : id?.startsWith('typed:')
                    ? { id: null, entered_name: id.slice(6) }
                    : null,
              });
            }}
          />
          <FieldHint>
            Unmapped names use the category code prefix. No team setup is required.
          </FieldHint>
          {error('team')}
        </div>
        {select('kit', 'kit_key', 'Kit')}
        <div className="space-y-1">
          <label className="block text-sm" htmlFor={`${prefix}-period-kind`}>
            Year or season <span className="text-xs text-muted-foreground">(optional)</span>
          </label>
          <Select
            id={`${prefix}-period-kind`}
            className="w-full"
            value={periodKind}
            helpLabel="Year or season (optional)"
            onChange={(e) => {
              setPeriodKind(e.target.value);
              update({ period: null });
            }}
          >
            <option value="">Not specified</option>
            <option value="calendar_year">Calendar year</option>
            <option value="season">Season</option>
          </Select>
          {periodKind && (
            <Input
              id={`${prefix}-period`}
              className="mt-2"
              value={value.period?.value || ''}
              maxLength={9}
              placeholder={periodKind === 'season' ? '2015/16' : '2015'}
              helpLabel={periodKind === 'season' ? 'Season (optional)' : 'Year (optional)'}
              aria-invalid={!!errors.period}
              aria-describedby={errors.period ? `${prefix}-period-error` : undefined}
              onChange={(e) =>
                update({
                  period: e.target.value
                    ? { kind: periodKind as 'season' | 'calendar_year', value: e.target.value }
                    : null,
                })
              }
            />
          )}
          {error('period')}
        </div>
        {select('collection', 'collection_key', 'Collection')}
        {select('sleeve', 'sleeve_key', 'Sleeve')}
        {select('collar', 'collar_key', 'Collar')}
      </div>
      <details className="rounded-xl border border-border p-3">
        <summary className="min-h-11 cursor-pointer py-2 text-sm font-medium">
          More jersey details{' '}
          <span className="font-normal text-muted-foreground">— all optional</span>
        </summary>
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          {(['player_name', 'shirt_number', 'kit_other_label', 'custom_design_text'] as const).map(
            (key, index) => (
              <div key={key} className="space-y-1">
                <label className="block text-sm" htmlFor={`${prefix}-${key}`}>
                  {
                    [
                      'Player name',
                      'Shirt number',
                      'Other kit description',
                      'Custom design details',
                    ][index]
                  }{' '}
                  <span className="text-xs text-muted-foreground">(optional)</span>
                </label>
                <Input
                  id={`${prefix}-${key}`}
                  value={value[key] ?? ''}
                  maxLength={key === 'shirt_number' ? 3 : key === 'player_name' ? 100 : 200}
                  inputMode={key === 'shirt_number' ? 'numeric' : undefined}
                  aria-invalid={!!errors[key]}
                  aria-describedby={errors[key] ? `${prefix}-${key}-error` : undefined}
                  helpLabel={
                    [
                      'Player name (optional)',
                      'Shirt number (optional)',
                      'Other kit description (optional)',
                      'Custom design details (optional)',
                    ][index]
                  }
                  onChange={(e) =>
                    update({
                      [key]: e.target.value || null,
                      ...(['player_name', 'shirt_number'].includes(key) && e.target.value
                        ? { print_mode: 'named' as const }
                        : {}),
                    })
                  }
                />
                {error(key)}
              </div>
            ),
          )}
          <div className="space-y-1">
            <label className="block text-sm" htmlFor={`${prefix}-print`}>
              Print <span className="text-xs text-muted-foreground">(optional)</span>
            </label>
            <Select
              id={`${prefix}-print`}
              className="w-full"
              value={value.print_mode || ''}
              helpLabel="Print (optional)"
              aria-invalid={!!errors.print_mode}
              aria-describedby={errors.print_mode ? `${prefix}-print_mode-error` : undefined}
              onChange={(e) =>
                update({ print_mode: (e.target.value || null) as JerseyDetailsV2['print_mode'] })
              }
            >
              <option value="">Not specified</option>
              <option value="named">Named print</option>
              <option value="no_name">No Name</option>
              <option value="customisable">Customisable</option>
            </Select>
            {error('print_mode')}
          </div>
          {select('edition', 'edition_key', 'Design edition')}
          {select('fit', 'fit_key', 'Fit')}
          {select('audience', 'audience_key', 'Audience')}
        </div>
      </details>
      {(value.player_name || value.shirt_number) &&
        !jerseyEntryPrintToken(value, context.identifier_config) && (
          <p
            role="status"
            className="rounded-xl border border-border bg-muted/40 p-3 text-xs text-muted-foreground"
          >
            Player details stay in the product name. This print has no saved SKU mapping, so Auto
            uses the standard placeholder for that part. You can also enter the SKU manually.
          </p>
        )}
    </section>
  );
}
